/**
 * The authenticated, post-activation shell.
 *
 * Fixed brand bar + header with greeting and "Sair", the push status row,
 * main <Outlet /> for nested routes,
 * and the fixed BottomTabBar. Mobile-first: max-width 34rem, centered.
 */
import { Outlet, useMatches } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { BottomTabBar } from '../components/BottomTabBar.tsx';
import { UpdatePrompt } from '../components/UpdatePrompt.tsx';
import { LocaleSelector } from '../components/LocaleSelector.tsx';
import { PushStatusWidget } from '../components/PushStatusWidget.tsx';
import { IconLogOut } from '../components/icons.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { getSupabaseClient } from '../api/supabase.ts';
import { isRouteId } from '../i18n/routes.ts';
import { fetchUnreadCount } from '../api/notifications.ts';
import { UNREAD_COUNT_QUERY_KEY } from '../api/notifications.ts';
import styles from './DashboardLayout.module.css';

export function DashboardLayout(): React.JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation();

  const name = auth.portalUser?.nome_exibicao ?? t('dashboard.fallbackName');

  // Read off the route `handle`, NOT the pathname: the slug is translated, so
  // matching on `/notificacoes` would silently stop working for a Chilean
  // customer at `/es-cl/notificaciones`. Same reason LocaleSelector does this.
  const matches = useMatches();
  const handle = matches[matches.length - 1]?.handle;
  const onNotificationsPage =
    typeof handle === 'object' &&
    handle !== null &&
    'routeId' in handle &&
    isRouteId(handle.routeId) &&
    handle.routeId === 'notificacoes';

  // `fetchUnreadCount` never throws (see `api/notifications.ts`); this query
  // function turns its handled error into a rejection so TanStack Query's own
  // error state applies, and `unreadCount` below simply stays undefined. A
  // failed count must degrade to NO badge, not crash the tab bar — see
  // BottomTabBar, which only renders a badge when the count is > 0.
  const { data: unreadCount } = useQuery({
    queryKey: UNREAD_COUNT_QUERY_KEY,
    queryFn: async () => {
      const result = await fetchUnreadCount(getSupabaseClient());
      if (result.error) throw new Error(result.error);
      return result.count;
    },
  });

  return (
    <div className={styles['shell']}>
      {/* Brand and header share one fixed container so they scroll together —
          two separately-fixed elements drift apart the moment either changes
          height. `.main`'s top margin is derived from --topbar-height, defined
          once in the stylesheet. */}
      <div className={styles['topbar']}>
        <div className={styles['brand']}>
          <img
            className={styles['brand-logo']}
            src="/brand/logo-international-horizontal.png"
            alt={t('brand.name')}
          />
        </div>

        <header className={styles['header']}>
          {/* ONE interpolated key. The old form concatenated "Ola, " with a
              span, which hardcodes name-last word order and cannot be reordered
              by a translator. */}
          <p className={styles['greeting']}>{t('dashboard.greeting', { name })}</p>
          <div className={styles['actions']}>
            <LocaleSelector />
            <button className={styles['logout']} onClick={() => void auth.logout()} type="button">
              <IconLogOut size={16} />
              {t('dashboard.logout')}
            </button>
          </div>
        </header>
      </div>

      <main className={styles['main']}>
        {/* Every page, not just the inbox. Whether notifications will actually
            arrive is a property of the app, not of one screen — and the states
            that matter (`blocked`, `lapsed`) are exactly the ones a customer
            would otherwise only discover by visiting Notificações, which is the
            screen they stop visiting when they think nothing is happening.

            The exception is `on`: an all-clear on every page is noise that
            teaches people to ignore the row. It shows only on Notificações,
            where someone would go to confirm it. */}
        <PushStatusWidget showWhenDeliverable={onNotificationsPage} />
        <Outlet />
      </main>

      {/* Above the tab bar. Without this the SW update handshake never
          fires and push silently dies on the next deploy — see UpdatePrompt. */}
      <UpdatePrompt />

      <BottomTabBar unreadCount={unreadCount ?? 0} />
    </div>
  );
}
